import { useState } from "react";
import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import {
  FaUsers,
  FaUser,
  FaCheck,
  FaInfoCircle,
  FaSignInAlt,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaMagic,
  FaImages,
  FaStar,
  FaRegClock,
} from "react-icons/fa";

// A coded (non-screenshot) recreation of the real Wobble customer-facing
// booking page — same header/stepper/calendar chrome as the actual app,
// including the live EN/HU language switch (a real Wobble feature, kept
// functional here rather than decorative). Skinned in Panni Pilates's own
// brand colors, since every Wobble booking page takes on the business's
// own branding rather than Wobble's.

const BRAND = {
  text: "#7a5e54",
  accent: "#7a5e54",
  accentMuted: "#d7b6ac",
  soft: "rgba(122, 94, 84, 0.08)",
  border: "#e6dcd3",
  page: "#f5f7fa",
};

// Selected/completed steps use the studio's secondary brand color;
// unselected steps and connecting lines stay neutral light gray.
const STEP_ACTIVE = "#7a5e54";
const STEP_INACTIVE = "#d5dbe0";

const STUDIO_NAME = "Panni Pilates";

type Lang = "hu" | "en";

const STRINGS: Record<Lang, Record<string, string>> = {
  hu: {
    chooseService: "Válassz szolgáltatást",
    continue: "Tovább a dátumhoz",
    individual: "Egyéni",
    group: "Csoportos",
    min: "perc",
    back: "Vissza",
    service: "Szolgáltatás",
    location: "Budapest",
    chooseDateTime: "Válassz dátumot és időpontot",
    chooseSpecialist: "Válassz szakembert",
    chooseDate: "Válassz dátumot",
    availableSlots: "Elérhető időpontok",
    yourDetails: "Adataid",
    name: "Név",
    email: "E-mail",
    phone: "Telefonszám",
    confirmBooking: "Foglalás megerősítése",
    confirmed: "Foglalás megerősítve!",
    newBooking: "Új foglalás indítása",
    at: "-kor",
    tabServices: "Szolgáltatások",
    tabAbout: "Rólunk",
    tabGallery: "Galéria",
    tabRatings: "Értékelések",
    month: "2026. szeptember",
  },
  en: {
    chooseService: "Choose a service",
    continue: "Continue to date & time",
    individual: "Individual",
    group: "Group",
    min: "min",
    back: "Back",
    service: "Service",
    location: "Budapest",
    chooseDateTime: "Choose a date and time",
    chooseSpecialist: "Choose a specialist",
    chooseDate: "Choose a date",
    availableSlots: "Available slots",
    yourDetails: "Your details",
    name: "Name",
    email: "Email",
    phone: "Phone number",
    confirmBooking: "Confirm booking",
    confirmed: "Booking confirmed!",
    newBooking: "Start a new booking",
    at: " at",
    tabServices: "Services",
    tabAbout: "About",
    tabGallery: "Gallery",
    tabRatings: "Ratings",
    month: "September 2026",
  },
};

const SERVICES = [
  {
    id: "core",
    type: "group" as const,
    icon: FaUsers,
    name: "Core Sculpt – Haladó csoportos Pilates",
    nameEn: "Core Sculpt – Advanced group Pilates",
    desc: "Dinamikusabb, intenzívebb Pilates óra",
    descEn: "A more dynamic, intense Pilates class",
    duration: 50,
    price: "5 500 Ft",
  },
  {
    id: "gentle",
    type: "group" as const,
    icon: FaUsers,
    name: "Gentle Balance – Csoportos kezdő Pilates",
    nameEn: "Gentle Balance – Beginner group Pilates",
    desc: "Nyugodt, támogató légkörben tanulhatod meg a Pilates alapjait",
    descEn: "Learn the basics of Pilates in a calm, supportive setting",
    duration: 50,
    price: "4 500 Ft",
  },
  {
    id: "private",
    type: "individual" as const,
    icon: FaUser,
    name: "Private Flow – Egyéni Pilates óra",
    nameEn: "Private Flow – Individual Pilates session",
    desc: "Teljesen rád szabott Pilates élmény",
    descEn: "A fully personalized Pilates experience",
    duration: 60,
    price: "16 990 Ft",
  },
];

const SPECIALISTS = ["Panni", "Kiss Réka"];
const SLOTS = ["09:00", "11:30", "14:00", "16:30", "18:00"];

// September 2026 — the 1st falls on a Tuesday (weekday index 1, Mon = 0).
// Availability follows a plain "every Tue & Wed" rule, same shape as the
// reference design, generated rather than hand-listed.
const FIRST_WEEKDAY = 1;
const DAYS_IN_MONTH = 30;
const CALENDAR_CELLS = Array.from({ length: 35 }, (_, i) => {
  const day = i - FIRST_WEEKDAY + 1;
  if (day < 1 || day > DAYS_IN_MONTH) return null;
  const weekday = (FIRST_WEEKDAY + (day - 1)) % 7;
  return { day, available: weekday === 1 || weekday === 2 };
});

const WEEKDAYS = { hu: ["H", "K", "Sze", "Cs", "P", "Szo", "V"], en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] };

const TABS = [
  { icon: FaMagic, key: "tabServices" },
  { icon: FaInfoCircle, key: "tabAbout" },
  { icon: FaImages, key: "tabGallery" },
  { icon: FaStar, key: "tabRatings" },
];

const STEP_COUNT = 4;

const WobbleBookingMock = () => {
  const [lang, setLang] = useState<Lang>("hu");
  const [step, setStep] = useState(0);
  const [service, setService] = useState<string | null>(null);
  const [specialist, setSpecialist] = useState<string | null>(null);
  const [date, setDate] = useState<number | null>(null);
  const [slot, setSlot] = useState<string | null>(null);

  const t = STRINGS[lang];
  const chosen = SERVICES.find((s) => s.id === service) ?? SERVICES[0];

  const goTo = (i: number) => setStep(Math.max(0, Math.min(STEP_COUNT - 1, i)));
  const pickSlot = (s: string) => {
    setSlot(s);
    goTo(2);
  };

  return (
    <Box
      borderRadius="32px"
      border="6px solid"
      borderColor="ink.900"
      bg="ink.900"
      overflow="hidden"
      boxShadow="0 26px 60px rgba(0, 72, 86, 0.32)"
      w="100%"
      sx={{ aspectRatio: "375 / 760" }}
    >
      <Flex direction="column" bg={BRAND.page} borderRadius="26px" overflow="hidden" h="100%">
        {/* Status bar */}
        <Flex justify="space-between" align="center" px={4} pt={2.5} pb={1} fontSize="9px" fontWeight="700" color="ink.900" flexShrink={0}>
          <Text>9:41</Text>
          <Box w="12px" h="7px" border="1px solid" borderColor="ink.900" borderRadius="1px" />
        </Flex>

        {/* Header card */}
        <Flex mx={3} mt={1} mb={3} bg="white" borderRadius="xl" boxShadow="0 2px 8px rgba(0,0,0,0.06)" px={2.5} py={2.5} justify="space-between" align="center" gap={1.5} flexShrink={0}>
          <Text fontFamily="heading" fontWeight="800" fontSize="10.5px" color={BRAND.text} noOfLines={1} minW={0} flex="1">
            {STUDIO_NAME}
          </Text>
          <Flex align="center" gap={1} flexShrink={0}>
            <Box
              as="button"
              type="button"
              onClick={() => setLang("en")}
              fontSize="7px"
              fontWeight="700"
              px={1.5}
              py={0.5}
              borderRadius="full"
              bg={lang === "en" ? BRAND.accent : "transparent"}
              color={lang === "en" ? "white" : BRAND.accent}
            >
              EN
            </Box>
            <Box as="button" type="button" onClick={() => setLang("hu")} fontSize="7px" fontWeight="700" color={lang === "hu" ? BRAND.accent : "slate.400"}>
              HU
            </Box>
            <Icon as={FaSignInAlt} boxSize="9px" color={BRAND.accent} ml={0.5} />
          </Flex>
        </Flex>

        {/* Step indicator */}
        <Flex align="center" justify="center" gap={1} px={4} mb={3} flexShrink={0}>
          {Array.from({ length: STEP_COUNT }).map((_, i) => (
            <Flex key={i} align="center" flex={i < STEP_COUNT - 1 ? "1" : "0 0 auto"}>
              <Flex
                w="20px"
                h="20px"
                borderRadius="full"
                align="center"
                justify="center"
                fontSize="8px"
                fontWeight="700"
                bg={i <= step ? STEP_ACTIVE : STEP_INACTIVE}
                color={i <= step ? "white" : "slate.500"}
                flexShrink={0}
              >
                {i < step ? <Icon as={FaCheck} boxSize="8px" /> : i + 1}
              </Flex>
              {i < STEP_COUNT - 1 && <Box flex="1" h="2px" bg={i < step ? STEP_ACTIVE : STEP_INACTIVE} mx={1} />}
            </Flex>
          ))}
        </Flex>

        {/* Content card */}
        <Box flex="1" minH={0} overflowY="auto" mx={3} mb={3} bg="white" borderRadius="xl" boxShadow="0 2px 8px rgba(0,0,0,0.06)" px={3.5} py={3}>
          {step === 0 && (
            <Box>
              <Text fontWeight="800" fontSize="xs" color="ink.900" mb={2.5}>
                {t.chooseService}
              </Text>
              <Flex direction="column" gap={2} mb={3}>
                {SERVICES.map((s) => (
                  <Flex
                    key={s.id}
                    as="button"
                    type="button"
                    onClick={() => setService(s.id)}
                    direction="column"
                    align="stretch"
                    textAlign="left"
                    border="1.5px solid"
                    borderColor={service === s.id ? BRAND.accent : BRAND.border}
                    bg={service === s.id ? BRAND.soft : "white"}
                    borderRadius="lg"
                    px={2.5}
                    py={2}
                    transition="all 0.2s ease"
                  >
                    <Flex justify="space-between" align="flex-start" mb={1}>
                      <Flex align="center" gap={2}>
                        <Flex w="26px" h="26px" borderRadius="full" bg={BRAND.accent} align="center" justify="center" flexShrink={0}>
                          <Icon as={s.icon} boxSize="11px" color="white" />
                        </Flex>
                        <Box>
                          <Text fontSize="10px" fontWeight="700" noOfLines={1}>
                            {lang === "hu" ? s.name : s.nameEn}
                          </Text>
                          <Flex align="center" gap={1}>
                            <Icon as={s.icon} boxSize="7px" color="slate.500" />
                            <Text fontSize="8px" color="slate.500">
                              {s.type === "individual" ? t.individual : t.group}
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>
                      <Icon as={FaInfoCircle} boxSize="10px" color="chalk.500" />
                    </Flex>
                    <Text fontSize="8px" color="slate.500" noOfLines={1} mb={1}>
                      {lang === "hu" ? s.desc : s.descEn}
                    </Text>
                    <Flex justify="space-between" align="center" fontSize="8px" color="slate.700">
                      <Flex align="center" gap={1}>
                        <Icon as={FaRegClock} boxSize="8px" />
                        <Text>
                          {s.duration} {t.min}
                        </Text>
                      </Flex>
                      <Text fontWeight="700">{s.price}</Text>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
              <Box
                as="button"
                type="button"
                disabled={!service}
                onClick={() => service && goTo(1)}
                w="100%"
                bg={service ? BRAND.accent : BRAND.accentMuted}
                color="white"
                fontWeight="700"
                fontSize="10px"
                borderRadius="full"
                py={2.5}
                cursor={service ? "pointer" : "not-allowed"}
                transition="all 0.2s ease"
              >
                {t.continue}
              </Box>
            </Box>
          )}

          {step === 1 && (
            <Box>
              <Flex align="center" justify="space-between" gap={2} mb={2.5}>
                <Flex as="button" type="button" onClick={() => goTo(0)} align="center" gap={1} fontSize="9px" color="slate.500" fontWeight="600" flexShrink={0}>
                  <Text>‹</Text>
                  <Text>{t.back}</Text>
                </Flex>
                <Text fontSize="9px" fontWeight="700" color="slate.600" flex="1" textAlign="right" noOfLines={1}>
                  {t.service} · {lang === "hu" ? chosen.name : chosen.nameEn}
                </Text>
              </Flex>

              <Flex align="center" gap={1.5} mb={2.5}>
                <Icon as={FaMapMarkerAlt} boxSize="9px" color={BRAND.accent} />
                <Text fontSize="9px" color={BRAND.accent} fontWeight="600">
                  {t.location}
                </Text>
              </Flex>

              <Text fontWeight="800" fontSize="xs" color="ink.900" mb={2.5}>
                {t.chooseDateTime}
              </Text>

              <Text fontSize="8px" fontWeight="700" color="slate.600" mb={1.5}>
                {t.chooseSpecialist}
              </Text>
              <Flex gap={1.5} wrap="wrap" mb={3}>
                {SPECIALISTS.map((sp) => (
                  <Box
                    key={sp}
                    as="button"
                    type="button"
                    onClick={() => setSpecialist(sp)}
                    fontSize="8px"
                    fontWeight="600"
                    px={2.5}
                    py={1.5}
                    borderRadius="full"
                    border="1.5px solid"
                    borderColor={specialist === sp ? BRAND.accent : BRAND.border}
                    color={specialist === sp ? BRAND.accent : "slate.600"}
                    bg={specialist === sp ? BRAND.soft : "white"}
                  >
                    {sp}
                  </Box>
                ))}
              </Flex>

              <Text fontSize="8px" fontWeight="700" color="slate.600" mb={1.5}>
                {t.chooseDate}
              </Text>
              <Box border="1px solid" borderColor={BRAND.border} borderRadius="lg" p={2} mb={3}>
                <Flex justify="space-between" align="center" mb={2}>
                  <Icon as={FaChevronLeft} boxSize="8px" color="slate.400" />
                  <Text fontSize="9px" fontWeight="700" color="ink.900">
                    {t.month}
                  </Text>
                  <Icon as={FaChevronRight} boxSize="8px" color="slate.400" />
                </Flex>
                <Flex justify="space-between" mb={1}>
                  {WEEKDAYS[lang].map((d) => (
                    <Text key={d} fontSize="6.5px" color="slate.400" w="24px" textAlign="center">
                      {d}
                    </Text>
                  ))}
                </Flex>
                <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap="2px">
                  {CALENDAR_CELLS.map((cell, i) =>
                    cell ? (
                      <Flex
                        key={i}
                        as="button"
                        type="button"
                        disabled={!cell.available}
                        onClick={() => cell.available && setDate(cell.day)}
                        h="22px"
                        align="center"
                        justify="center"
                        borderRadius="full"
                        fontSize="8px"
                        fontWeight={cell.available ? "700" : "500"}
                        bg={date === cell.day ? BRAND.accent : "transparent"}
                        color={date === cell.day ? "white" : cell.available ? "ink.900" : "chalk.400"}
                        cursor={cell.available ? "pointer" : "default"}
                      >
                        {cell.day}
                      </Flex>
                    ) : (
                      <Box key={i} />
                    )
                  )}
                </Box>
              </Box>

              {date && (
                <Box>
                  <Text fontSize="8px" fontWeight="700" color="slate.600" mb={1.5}>
                    {t.availableSlots}
                  </Text>
                  <Flex wrap="wrap" gap={1.5}>
                    {SLOTS.map((s) => (
                      <Box
                        key={s}
                        as="button"
                        type="button"
                        onClick={() => pickSlot(s)}
                        border="1.5px solid"
                        borderColor={slot === s ? BRAND.accent : BRAND.border}
                        bg={slot === s ? BRAND.accent : "white"}
                        color={slot === s ? "white" : "ink.900"}
                        borderRadius="full"
                        px={2.5}
                        py={1}
                        fontSize="9px"
                        fontWeight="600"
                        transition="all 0.2s ease"
                      >
                        {s}
                      </Box>
                    ))}
                  </Flex>
                </Box>
              )}
            </Box>
          )}

          {step === 2 && (
            <Box>
              <Text fontWeight="800" fontSize="xs" color="ink.900" mb={2.5}>
                {t.yourDetails}
              </Text>
              <Flex direction="column" gap={2} mb={3}>
                {[
                  { label: t.name, value: "Kovács Anna" },
                  { label: t.email, value: "kovacs.anna@email.hu" },
                  { label: t.phone, value: "+36 30 123 4567" },
                ].map((f) => (
                  <Box key={f.label} border="1px solid" borderColor={BRAND.border} borderRadius="lg" px={2.5} py={1.5}>
                    <Text fontSize="7px" color="slate.500">
                      {f.label}
                    </Text>
                    <Text fontSize="9px" fontWeight="600" color="ink.900">
                      {f.value}
                    </Text>
                  </Box>
                ))}
              </Flex>
              <Box as="button" type="button" onClick={() => goTo(3)} w="100%" bg={BRAND.accent} color="white" fontWeight="700" fontSize="10px" borderRadius="full" py={2.5}>
                {t.confirmBooking}
              </Box>
            </Box>
          )}

          {step === 3 && (
            <Flex direction="column" align="center" textAlign="center" pt={4}>
              <Flex w="44px" h="44px" borderRadius="full" bg={BRAND.soft} align="center" justify="center" mb={3}>
                <Icon as={FaCheck} boxSize="18px" color={BRAND.accent} />
              </Flex>
              <Text fontWeight="800" fontSize="sm" color="ink.900" mb={1.5}>
                {t.confirmed}
              </Text>
              <Text fontSize="9px" color="slate.500" mb={3}>
                {lang === "hu" ? chosen.name : chosen.nameEn}: {date ?? 1}. {slot ? `${slot}${t.at}` : ""}
              </Text>
              <Box
                as="button"
                type="button"
                onClick={() => {
                  goTo(0);
                  setDate(null);
                  setSlot(null);
                }}
                fontSize="9px"
                fontWeight="700"
                color={BRAND.accent}
                borderBottom="1px solid"
                borderColor={BRAND.accent}
              >
                {t.newBooking}
              </Box>
            </Flex>
          )}
        </Box>

        {/* Bottom tab bar */}
        <Flex justify="space-around" bg="white" borderTop="1px solid" borderColor={BRAND.border} py={2} flexShrink={0}>
          {TABS.map((tab, i) => (
            <Flex key={tab.key} direction="column" align="center" gap={0.5} color={i === 0 ? BRAND.accent : "slate.400"}>
              <Icon as={tab.icon} boxSize="12px" />
              <Text fontSize="6.5px" fontWeight="600">
                {t[tab.key]}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};

export default WobbleBookingMock;
